'use client';

import { c as _c } from "react/compiler-runtime";
import type { EngineAnchor } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useEngineAnchor';
import { ENGINE_BACKDROP_CONFIG } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/config';
import type { EngineBackdropConfig } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/config/types';
import { DotGridGround } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/dot-grid-ground';
import { PowerBurst } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/power-burst';
import type { MutableRef } from '@/app/lcv/components/sections/spartan/shared/webgpu/canvas-basic/types';
import { NEAR_ZERO_3 } from '@/app/lcv/components/sections/spartan/shared/webgpu/constants';
export interface EngineBackdropProps {
  anchorRef?: MutableRef<EngineAnchor>;
  burstKey?: number;
  // 0..1 master fade for the whole backdrop, read per frame.
  //
  // A ref rather than a prop because the page tweens it on every section
  // change, and as state that would re-render this subtree at frame rate.
  // Fading rather than unmounting, because unmounting rebuilds both
  // materials and recompiling those shaders is a visible hitch on the way
  // back.
  visibilityRef?: MutableRef<number>;
  // 0..1 along the grid's heat palette -- see the config's `colorMidTo`
  // pair. Left unset the grid holds its resting colours.
  heatRef?: MutableRef<number>;
  // Bump to re-run the reveal sweep -- the backdrop making an entrance
  // again on a section it has already played in.
  replayKey?: number;
  // Draws both materials for a few frames while they are still invisible,
  // so their WebGPU pipelines are compiled before the frame that first
  // shows them. See DotGridGroundProps.warmUp.
  warmUp?: boolean;
  isAnimating?: boolean;
  config?: EngineBackdropConfig;
}

// Dot-grid atmosphere with a brief engine-settle power burst. The engine aura remains
// anchored independently inside EngineStage.
export const EngineBackdrop = t0 => {
  const $ = _c(60);
  const {
    visibilityRef,
    anchorRef,
    burstKey,
    heatRef,
    replayKey,
    warmUp: t1,
    isAnimating: t2,
    config: t3
  } = t0;
  const warmUp = t1 === undefined ? false : t1;
  const isAnimating = t2 === undefined ? true : t2;
  const config = t3 === undefined ? ENGINE_BACKDROP_CONFIG : t3;
  const {
    ground
  } = config;
  const {
    projection,
    grid,
    idle,
    reveal,
    flash
  } = ground;
  const revealSpeed = reveal.reach / Math.max(reveal.sweepSeconds, NEAR_ZERO_3);
  let t4;
  if ($[0] !== anchorRef || $[1] !== burstKey || $[2] !== isAnimating || $[3] !== visibilityRef || $[4] !== warmUp) {
    t4 = <PowerBurst visibilityRef={visibilityRef} burstKey={burstKey} anchorRef={anchorRef} warmUp={warmUp} isAnimating={isAnimating} />;
    $[0] = anchorRef;
    $[1] = burstKey;
    $[2] = isAnimating;
    $[3] = visibilityRef;
    $[4] = warmUp;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [0, 0];
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== flash.decay || $[8] !== flash.strength || $[9] !== grid.brightness || $[10] !== grid.colorHigh || $[11] !== grid.colorHighTo || $[12] !== grid.colorLow || $[13] !== grid.colorLowTo || $[14] !== grid.colorMid || $[15] !== grid.colorMidTo || $[16] !== grid.compression || $[17] !== grid.count || $[18] !== grid.dotCeiling || $[19] !== grid.dotRadius || $[20] !== grid.dotSoftness || $[21] !== grid.fadeEnd || $[22] !== grid.fadeStart || $[23] !== grid.markArmLength || $[24] !== grid.markArmThickness || $[25] !== grid.markBoost || $[26] !== grid.markEvery || $[27] !== grid.opacity || $[28] !== heatRef || $[29] !== idle.crestFade || $[30] !== idle.fadeInSeconds || $[31] !== idle.frequency || $[32] !== idle.glow || $[33] !== idle.height || $[34] !== idle.noiseMix || $[35] !== idle.noiseScale || $[36] !== idle.noiseSpeed || $[37] !== idle.speed || $[38] !== idle.troughFade || $[39] !== idle.warp || $[40] !== isAnimating || $[41] !== projection.horizon || $[42] !== projection.horizonFeather || $[43] !== projection.panX || $[44] !== projection.panY || $[45] !== projection.pitch || $[46] !== projection.rollDeg || $[47] !== projection.scale || $[48] !== projection.segments || $[49] !== replayKey || $[50] !== reveal.softness || $[51] !== reveal.turnOnDecay || $[52] !== reveal.turnOnStrength || $[53] !== revealSpeed || $[54] !== visibilityRef || $[55] !== warmUp) {
    t6 = <DotGridGround replayKey={replayKey} isAnimating={isAnimating} visibilityRef={visibilityRef} warmUp={warmUp} heatRef={heatRef} pitch={projection.pitch} rollDeg={projection.rollDeg} horizon={projection.horizon} horizonFeather={projection.horizonFeather} panX={projection.panX} panY={projection.panY} projectionScale={projection.scale} segments={projection.segments} origin={t5} gridCount={grid.count} gridCompression={grid.compression} dotRadius={grid.dotRadius} dotSoftness={grid.dotSoftness} markEvery={grid.markEvery} markArmLength={grid.markArmLength} markArmThickness={grid.markArmThickness} markBoost={grid.markBoost} fadeStart={grid.fadeStart} fadeEnd={grid.fadeEnd} brightness={grid.brightness} dotCeiling={grid.dotCeiling} opacity={grid.opacity} colorLow={grid.colorLow} colorMid={grid.colorMid} colorHigh={grid.colorHigh} colorLowTo={grid.colorLowTo} colorMidTo={grid.colorMidTo} colorHighTo={grid.colorHighTo} flashStrength={flash.strength} flashDecay={flash.decay} revealSpeed={revealSpeed} revealSoftness={reveal.softness} turnOnStrength={reveal.turnOnStrength} turnOnDecay={reveal.turnOnDecay} idleHeight={idle.height} idleFrequency={idle.frequency} idleSpeed={idle.speed} idleGlow={idle.glow} idleFade={idle.crestFade} idleTroughFade={idle.troughFade} idleWarp={idle.warp} idleNoiseScale={idle.noiseScale} idleNoiseSpeed={idle.noiseSpeed} idleNoiseMix={idle.noiseMix} idleFadeInSeconds={idle.fadeInSeconds} />;
    $[7] = flash.decay;
    $[8] = flash.strength;
    $[9] = grid.brightness;
    $[10] = grid.colorHigh;
    $[11] = grid.colorHighTo;
    $[12] = grid.colorLow;
    $[13] = grid.colorLowTo;
    $[14] = grid.colorMid;
    $[15] = grid.colorMidTo;
    $[16] = grid.compression;
    $[17] = grid.count;
    $[18] = grid.dotCeiling;
    $[19] = grid.dotRadius;
    $[20] = grid.dotSoftness;
    $[21] = grid.fadeEnd;
    $[22] = grid.fadeStart;
    $[23] = grid.markArmLength;
    $[24] = grid.markArmThickness;
    $[25] = grid.markBoost;
    $[26] = grid.markEvery;
    $[27] = grid.opacity;
    $[28] = heatRef;
    $[29] = idle.crestFade;
    $[30] = idle.fadeInSeconds;
    $[31] = idle.frequency;
    $[32] = idle.glow;
    $[33] = idle.height;
    $[34] = idle.noiseMix;
    $[35] = idle.noiseScale;
    $[36] = idle.noiseSpeed;
    $[37] = idle.speed;
    $[38] = idle.troughFade;
    $[39] = idle.warp;
    $[40] = isAnimating;
    $[41] = projection.horizon;
    $[42] = projection.horizonFeather;
    $[43] = projection.panX;
    $[44] = projection.panY;
    $[45] = projection.pitch;
    $[46] = projection.rollDeg;
    $[47] = projection.scale;
    $[48] = projection.segments;
    $[49] = replayKey;
    $[50] = reveal.softness;
    $[51] = reveal.turnOnDecay;
    $[52] = reveal.turnOnStrength;
    $[53] = revealSpeed;
    $[54] = visibilityRef;
    $[55] = warmUp;
    $[56] = t6;
  } else {
    t6 = $[56];
  }
  let t7;
  if ($[57] !== t4 || $[58] !== t6) {
    t7 = <>{t4}{t6}</>;
    $[57] = t4;
    $[58] = t6;
    $[59] = t7;
  } else {
    t7 = $[59];
  }
  return t7;
};